import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { initFirebase } from '@paulownia/shared/lib/firebase';
import { onTenantSnapshot } from '@paulownia/shared/services/tenant';
import { Button } from '@paulownia/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@paulownia/shared/components/ui/card';
import { Switch } from '@paulownia/shared/components/ui/switch';
import { Badge } from '@paulownia/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';
import {
  Store,
  BarChart3,
  FlaskConical,
  Layers,
  RefreshCw,
  Utensils,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  updateTenantIsActive,
  updateTenantFeature,
  updateTenantActivityTypes,
} from '@paulownia/shared/services/tenant';
import { getOrderSourceStats, type OrderSourceStats } from '@paulownia/shared/services/orders';
import type { TenantFeatures, ActivityType } from '@paulownia/shared';
import { ACTIVITY_TYPE_LABELS } from '@paulownia/shared';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  settings?: {
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
  };
  features?: TenantFeatures;
  activityTypes?: ActivityType[];
}

interface FeatureConfig {
  key: keyof TenantFeatures;
  label: string;
  description: string;
}

// Feature flags that are still in beta. When a feature graduates to GA, remove it from here.
// The newProductFlow feature has graduated to GA and is now enabled for all tenants.
const AVAILABLE_FEATURES: FeatureConfig[] = [];

export const TenantSettingsAdmin = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [savingFeature, setSavingFeature] = useState<keyof TenantFeatures | null>(null);
  const [stats, setStats] = useState<OrderSourceStats | null>(null);
  const [unknownOrderIds, setUnknownOrderIds] = useState<string[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [savingActivityTypes, setSavingActivityTypes] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  // Listen to tenant document for real-time updates
  useEffect(() => {
    if (!selectedTenantId) {
      setSelectedTenant(null);
      setStats(null);
      return;
    }

    const unsubscribe = onTenantSnapshot(
      selectedTenantId,
      (tenant) => {
        if (tenant) {
          setSelectedTenant({
            id: tenant.id,
            name: tenant.name,
            slug: tenant.slug,
            isActive: tenant.isActive,
            settings: tenant.settings,
            features: tenant.features,
            activityTypes: tenant.activityTypes || [],
          });
        } else {
          setSelectedTenant(null);
        }
      },
      (error) => {
        console.error('Error listening to tenant:', error);
        toast.error('Erreur lors du chargement du tenant');
      }
    );

    // Load stats
    loadStats(selectedTenantId);

    return () => unsubscribe();
  }, [selectedTenantId]);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const { functions } = initFirebase();
      const listAllTenants = httpsCallable(functions, 'listAllTenants');
      const result = await listAllTenants();
      const data = result.data as { success: boolean; tenants: Tenant[] };

      if (data.success) {
        setTenants(data.tenants);
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
      toast.error('Erreur lors du chargement des tenants');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (tenantId: string) => {
    setStatsLoading(true);
    try {
      const response = await getOrderSourceStats(tenantId);
      setStats(response.stats);
      setUnknownOrderIds(response.unknownOrderIds);
    } catch (error) {
      console.error('Error loading order stats:', error);
      setStats(null);
      setUnknownOrderIds([]);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleVisibilityChange = async (checked: boolean) => {
    if (!selectedTenantId) return;

    setSavingVisibility(true);
    try {
      await updateTenantIsActive(selectedTenantId, checked);
      toast.success(
        checked
          ? 'Le tenant est maintenant visible sur le portail'
          : 'Le tenant est maintenant masque du portail'
      );
    } catch (error) {
      console.error('Error updating visibility:', error);
      const err = error as { code?: string; message?: string };

      if (err.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else {
        toast.error(err.message || 'Erreur lors de la mise a jour');
      }
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleToggleFeature = async (feature: keyof TenantFeatures, enabled: boolean) => {
    if (!selectedTenantId) return;

    setSavingFeature(feature);
    try {
      await updateTenantFeature(selectedTenantId, feature, enabled);
      toast.success(
        enabled
          ? 'Fonctionnalite beta activee'
          : 'Fonctionnalite beta desactivee'
      );
    } catch (error) {
      console.error('Error updating feature:', error);
      const err = error as { code?: string; message?: string };

      if (err.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Verifiez que vous etes superadmin.');
      } else {
        toast.error(err.message || 'Erreur lors de la mise a jour');
      }
    } finally {
      setSavingFeature(null);
    }
  };

  const handleToggleActivityType = async (activityType: ActivityType) => {
    if (!selectedTenantId || !selectedTenant) return;

    const currentTypes = selectedTenant.activityTypes || [];
    const newTypes = currentTypes.includes(activityType)
      ? currentTypes.filter((t) => t !== activityType)
      : [...currentTypes, activityType];

    setSavingActivityTypes(true);
    try {
      await updateTenantActivityTypes(selectedTenantId, newTypes);
      toast.success('Types d\'activite mis a jour');
    } catch (error) {
      console.error('Error updating activity types:', error);
      const err = error as { code?: string; message?: string };

      if (err.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else {
        toast.error(err.message || 'Erreur lors de la mise a jour');
      }
    } finally {
      setSavingActivityTypes(false);
    }
  };

  // Get sorted sections and subsections from settings
  const getSortedSections = (): string[] => {
    if (!selectedTenant?.settings?.sectionOrder) return [];

    const entries = Object.entries(selectedTenant.settings.sectionOrder);
    return entries
      .sort((a, b) => a[1] - b[1])
      .map(([section]) => section);
  };

  const getSortedSubSections = (section: string): string[] => {
    if (!selectedTenant?.settings?.subSectionOrder?.[section]) return [];

    const entries = Object.entries(selectedTenant.settings.subSectionOrder[section]);
    return entries
      .sort((a, b) => a[1] - b[1])
      .map(([subSection]) => subSection);
  };

  const sortedSections = getSortedSections();
  const hasSubSections = selectedTenant?.settings?.subSectionOrder &&
    Object.keys(selectedTenant.settings.subSectionOrder).length > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Parametres des Tenants</h2>
      </div>

      {/* Tenant Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Selectionner un tenant</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedTenantId}
            onValueChange={setSelectedTenantId}
            disabled={loading}
          >
            <SelectTrigger className="w-full md:w-96">
              <SelectValue placeholder="Choisir un tenant..." />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedTenant && (
        <>
          {/* Portal Visibility */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                Visibilite sur le portail
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Controlez si ce tenant apparait dans la liste des marchands sur le portail client.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium">Referencement actif</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedTenant.isActive
                      ? 'Le tenant est visible et les clients peuvent passer commande.'
                      : 'Le tenant est masque du portail. Les clients avec le lien direct peuvent toujours commander.'}
                  </p>
                </div>
                <Switch
                  checked={selectedTenant.isActive}
                  onCheckedChange={handleVisibilityChange}
                  disabled={savingVisibility}
                />
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>Slug:</strong> {selectedTenant.slug}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Activity Types */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Utensils className="h-5 w-5" />
                Types d'activite
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Definissez le(s) type(s) d'activite de ce commercant. Ces informations sont affichees sur la page marche.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {(Object.entries(ACTIVITY_TYPE_LABELS) as [ActivityType, string][]).map(
                  ([type, label]) => {
                    const isSelected = selectedTenant.activityTypes?.includes(type) ?? false;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleToggleActivityType(type)}
                        disabled={savingActivityTypes}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          isSelected
                            ? 'bg-orange-100 border-orange-300 text-orange-800'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        } ${savingActivityTypes ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {label}
                      </button>
                    );
                  }
                )}
              </div>
              {selectedTenant.activityTypes && selectedTenant.activityTypes.length > 0 && (
                <p className="mt-4 text-sm text-muted-foreground">
                  Selection actuelle:{' '}
                  <span className="font-medium">
                    {selectedTenant.activityTypes
                      .map((t) => ACTIVITY_TYPE_LABELS[t])
                      .join(' · ')}
                  </span>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Order Statistics */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Statistiques des commandes
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => loadStats(selectedTenantId)}
                  disabled={statsLoading}
                  className="ml-auto"
                >
                  <RefreshCw className={`h-4 w-4 ${statsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Repartition des commandes par source (client ou commercant).
              </p>
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <p className="text-center text-muted-foreground py-4">Chargement...</p>
              ) : stats ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{stats.customer}</p>
                      <p className="text-sm text-blue-600">Clients</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{stats.merchant}</p>
                      <p className="text-sm text-green-600">Commercant</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
                      <p className="text-sm text-gray-600">Total</p>
                    </div>
                  </div>
                  {stats.unknown > 0 && (
                    <div className="text-xs text-muted-foreground text-center space-y-1">
                      <p>
                        {stats.unknown} commande{stats.unknown > 1 ? 's' : ''} anterieure{stats.unknown > 1 ? 's' : ''} sans source enregistree
                      </p>
                      {unknownOrderIds.length > 0 && (
                        <p className="font-mono text-[10px] bg-gray-100 p-2 rounded">
                          ID{unknownOrderIds.length > 1 ? 's' : ''}: {unknownOrderIds.join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  Impossible de charger les statistiques
                </p>
              )}
            </CardContent>
          </Card>

          {/* Beta Features */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5" />
                Fonctionnalites Beta
                <Badge variant="outline" className="ml-2 text-xs font-normal">
                  Superadmin
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Activez ou desactivez les fonctionnalites en cours de developpement pour ce tenant.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {AVAILABLE_FEATURES.map((feature) => (
                <div
                  key={feature.key}
                  className="flex items-center justify-between py-2"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{feature.label}</p>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                  <Switch
                    checked={selectedTenant.features?.[feature.key] ?? false}
                    onCheckedChange={(checked) => handleToggleFeature(feature.key, checked)}
                    disabled={savingFeature !== null}
                  />
                </div>
              ))}

              {AVAILABLE_FEATURES.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  Aucune fonctionnalite beta disponible actuellement.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Section Order (View Only) */}
          {sortedSections.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Ordre des sections
                  <Badge variant="secondary" className="ml-2 text-xs font-normal">
                    Lecture seule
                  </Badge>
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-2">
                  L'ordre des sections configure pour ce tenant. Pour modifier, allez dans les parametres du tenant.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {sortedSections.map((section, index) => (
                    <div
                      key={section}
                      className="flex items-center gap-3 bg-gray-50 border rounded-lg p-3"
                    >
                      <span className="text-sm font-medium text-gray-500 w-6">
                        {index + 1}.
                      </span>
                      <span className="font-medium">
                        {section.charAt(0).toUpperCase() + section.slice(1)}
                      </span>
                      {hasSubSections && selectedTenant.settings?.subSectionOrder?.[section] && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          {getSortedSubSections(section).length} sous-section(s)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* SubSection Order (View Only) */}
          {hasSubSections && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Ordre des sous-sections
                  <Badge variant="secondary" className="ml-2 text-xs font-normal">
                    Lecture seule
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {sortedSections
                  .filter((section) => getSortedSubSections(section).length > 0)
                  .map((section) => (
                    <div key={section} className="space-y-2">
                      <h4 className="font-medium text-gray-700 border-b pb-2">
                        {section.charAt(0).toUpperCase() + section.slice(1)}
                      </h4>
                      <div className="space-y-1 pl-4">
                        {getSortedSubSections(section).map((subSection, index) => (
                          <div
                            key={subSection}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <span className="text-gray-400">{index + 1}.</span>
                            <span>{subSection.charAt(0).toUpperCase() + subSection.slice(1)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
